// src/modules/app-views/toolbox/pages/Ai/helpers/index.js
import translateX from 'utils/translateX';

/**
 * Build user message content from form values
 */
export function buildUserMessageContent(values) {
  const { toolDescription } = values;
  let content = `${toolDescription}`;
  return content;
}

/**
 * Format price to BRL currency
 */
export function formatPrice(value, showZeroIfEmpty = false) {
  if (value === null || value === undefined || isNaN(value)) {
    if (showZeroIfEmpty) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format(0);
    }
    return '';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}

/**
 * Format price compact (always shows value)
 */
export function formatPriceCompact(value) {
  if (value === null || value === undefined || isNaN(value)) {
    return 'R$ 0,00';
  }
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Build natural language intro for cataloged tool
 */
export function buildNaturalIntro(tool, alreadyExists) {
  const name = tool?.shortName || tool?.name || '';

  let intro = '';

  if (alreadyExists) {
    intro = translateX('ai_intro_tool_already_exists', { name });
  } else {
    intro = translateX('ai_intro_found_tool', { name });
  }

  // Add category if available
  const category = tool?.classification?.category;
  if (category) {
    intro += ' ' + translateX('ai_intro_category', { category });
  }

  return intro;
}

/**
 * Build error details from API response
 */
export function buildErrorDetails({ message, code, details, statusCode, isNetworkError }) {
  const result = {
    explanationKey: '',
    explanationFallback: '',
    technicalMessage: message || '',
    suggestionKeys: [],
  };

  // Network errors
  if (isNetworkError) {
    result.explanationKey = 'error_network_connection';
    result.suggestionKeys = ['suggestion_check_internet', 'suggestion_try_again_later'];
    return result;
  }

  // Handle specific error codes
  if (code) {
    switch (code) {
      case 'AI_SERVICE_UNAVAILABLE':
        result.explanationKey = 'error_ai_service_unavailable';
        result.suggestionKeys = ['suggestion_try_different_model', 'suggestion_try_again_later'];
        break;
      case 'AI_RATE_LIMIT':
        result.explanationKey = 'error_ai_rate_limit';
        result.suggestionKeys = ['suggestion_wait_few_minutes', 'suggestion_try_different_model'];
        break;
      case 'AI_CONTEXT_TOO_LONG':
        result.explanationKey = 'error_ai_context_too_long';
        result.suggestionKeys = ['suggestion_simplify_description'];
        break;
      case 'AI_INVALID_RESPONSE':
        result.explanationKey = 'error_ai_invalid_response';
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_try_different_model'];
        break;
      case 'INSUFFICIENT_CREDITS':
        result.explanationKey = 'error_insufficient_credits';
        result.suggestionKeys = ['suggestion_add_credits', 'suggestion_contact_support'];
        break;
      default:
        result.explanationKey = 'error_processing_request';
        result.technicalMessage = `[${code}] ${message || ''}`;
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Handle HTTP status codes
  if (statusCode) {
    switch (statusCode) {
      case 400:
        result.explanationKey = 'error_invalid_request';
        result.suggestionKeys = ['suggestion_check_product_data', 'suggestion_verify_required_fields'];
        break;
      case 401:
      case 403:
        result.explanationKey = 'error_authentication';
        result.suggestionKeys = ['suggestion_refresh_page', 'suggestion_login_again'];
        break;
      case 429:
        result.explanationKey = 'error_too_many_requests';
        result.suggestionKeys = ['suggestion_wait_few_minutes'];
        break;
      case 500:
      case 502:
      case 503:
        result.explanationKey = 'error_server_error';
        result.suggestionKeys = ['suggestion_try_again_later', 'suggestion_contact_support_if_persists'];
        break;
      default:
        result.explanationKey = 'error_unexpected';
        result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Fallback
  if (message) {
    const lowerMessage = message.toLowerCase();
    if (lowerMessage.includes('timeout') || lowerMessage.includes('tempo')) {
      result.explanationKey = 'error_timeout';
      result.suggestionKeys = ['suggestion_simplify_description', 'suggestion_try_again'];
    } else {
      result.explanationFallback = message;
      result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support_if_persists'];
    }
    return result;
  }

  // Ultimate fallback
  result.explanationKey = 'error_unexpected';
  result.suggestionKeys = ['suggestion_try_again', 'suggestion_contact_support'];

  return result;
}
